import MarkdownIt from 'markdown-it';

const md = new MarkdownIt();

// Block-level (or self-contained) HTML tags that indicate the content is
// already HTML. A stray inline tag (e.g. a lone <br>) inside AI-generated
// markdown should NOT stop markdown conversion, or the rest renders raw.
// TipTap @-mention spans are inline and must not block conversion.
const BLOCK_HTML_TAG =
  /<(p|div|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|blockquote|pre|img|hr|section|article|figure)\b/i;

const MENTION_SPAN_RE =
  /<span\b[^>]*\bdata-type=["']mention["'][^>]*>[\s\S]*?<\/span>/gi;
const LAYER_EMBED_RE =
  /<div\b[^>]*\bdata-type=["']report-layer-embed["'][^>]*>[\s\S]*?<\/div>/gi;

const hasMarkdownMarkers = (content: string): boolean =>
  /[*_#\[\]`]/.test(content);

/**
 * Swap TipTap mention spans for opaque placeholders so markdown-it does not
 * mangle them; restore after render.
 */
function protectMentions(content: string): {
  text: string;
  mentions: string[];
} {
  const mentions: string[] = [];
  const withMentions = content.replace(MENTION_SPAN_RE, (match) => {
    const index = mentions.length;
    mentions.push(match);
    return `@@MENTION_${index}@@`;
  });
  const text = withMentions.replace(LAYER_EMBED_RE, (match) => {
    const index = mentions.length;
    mentions.push(match);
    return `@@MENTION_${index}@@`;
  });
  return { text, mentions };
}

function restoreMentions(html: string, mentions: string[]): string {
  return mentions.reduce(
    (acc, mention, index) => acc.replace(`@@MENTION_${index}@@`, mention),
    html
  );
}

const shouldRenderMarkdown = (content: string): boolean => {
  if (BLOCK_HTML_TAG.test(content)) {
    return false;
  }
  return hasMarkdownMarkers(content);
};

export const normalizeMarkdown = (content: string): string => {
  if (!content) return content;

  const { text, mentions } = protectMentions(content);

  // Mentions alone, plain text, or already block HTML: leave as-is.
  if (!shouldRenderMarkdown(text)) {
    return content;
  }

  const rendered = md.render(text);
  return restoreMentions(rendered, mentions);
};
